import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Upload, Loader2, Send, BadgeCheck, MessageCircle, FileText, User, MapPin, GraduationCap } from "lucide-react";
import { PageHeader } from "@/pages/ScholarshipExam";
import { COURSES, WHATSAPP_LINK } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-md border border-brand-line bg-brand-bg px-4 py-3 text-sm text-brand-ink outline-none transition-colors duration-300 placeholder:text-brand-muted/60 focus:border-brand-green";

const labelClass = "mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted";

const GENDERS = ["Male", "Female", "Other"];
const RELIGIONS = ["Hindu", "Muslim", "Sikh", "Christian", "Jain", "Buddhist", "Other"];

function Field({ label, children, span }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <span className={labelClass}>{label}</span>
      {children}
    </div>
  );
}

function UploadBox({ label, hint, required, value, onPick, testId }) {
  const ref = useRef(null);
  return (
    <div>
      <span className={labelClass}>{label} {required && "*"}</span>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden" onChange={onPick} />
      <button
        type="button"
        data-testid={testId}
        onClick={() => ref.current?.click()}
        className={`flex w-full items-center justify-center gap-3 rounded-md border-2 border-dashed px-4 py-7 text-sm transition-colors ${
          value
            ? "border-brand-green bg-brand-green/5 text-brand-green"
            : "border-brand-line bg-brand-bg text-brand-muted hover:border-brand-green hover:text-brand-green"
        }`}
      >
        {value?.preview ? (
          <img src={value.preview} alt="preview" className="h-16 w-16 rounded object-cover" />
        ) : (
          <Upload className="h-5 w-5" />
        )}
        <span className="max-w-[220px] truncate">{value ? value.name : hint}</span>
      </button>
    </div>
  );
}

export default function AdmissionForm() {
  const [form, setForm] = useState({
    name: "", dob: "", gender: GENDERS[0], religion: RELIGIONS[0], phone: "", email: "",
    address: "", city: "", state: "", pincode: "", course: COURSES[0].title,
  });
  const [photo, setPhoto] = useState(null);
  const [cert10, setCert10] = useState(null);
  const [cert12, setCert12] = useState(null);
  const [idProof, setIdProof] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pick = (setter, isPhoto) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (isPhoto && !/\.(png|jpe?g)$/i.test(f.name)) {
      toast.error("Photo must be a PNG or JPG image.");
      return;
    }
    if (!/\.(png|jpe?g|pdf)$/i.test(f.name)) {
      toast.error("Only PNG, JPG or PDF files are allowed.");
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Each file must be under 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setter({ name: f.name, data: reader.result, preview: isPhoto ? reader.result : null });
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cert10 || !idProof) {
      toast.error("Please upload your 10th certificate and Aadhaar (ID proof).");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admission/apply`, {
        ...form,
        photo: photo?.data || null, photo_name: photo?.name || null,
        cert10: cert10.data, cert10_name: cert10.name,
        cert12: cert12?.data || null, cert12_name: cert12?.name || null,
        id_proof: idProof.data, id_proof_name: idProof.name,
      });
      setDone({ id: data.application_id });
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admission-page" className="min-h-screen bg-brand-bg font-sans text-brand-ink antialiased">
      <PageHeader />

      <section className="bg-brand-green py-16 text-white md:py-20">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-[#E8C4B0]/40 px-5 py-2 font-mono text-xs uppercase tracking-[0.22em] text-[#E8C4B0]"
          >
            <GraduationCap className="h-4 w-4" /> Admissions Open 2026–27
          </motion.p>
          <motion.h1
            data-testid="admission-headline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="mt-6 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl"
          >
            Online <em className="italic text-[#E8C4B0]">Admission Form</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-white/75 md:text-base"
          >
            Fill this 3-minute form, upload your documents, and our admissions team
            will call you within 24 hours to confirm your seat.
          </motion.p>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-6 py-14 md:py-16">
        {done ? (
          <motion.div
            data-testid="admission-success"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="border border-brand-line bg-brand-surface p-10 text-center shadow-sm md:p-14"
          >
            <BadgeCheck className="mx-auto h-14 w-14 text-brand-green" />
            <h2 className="mt-5 font-serif text-3xl font-light md:text-4xl">
              Application <em className="italic text-brand-terra">received</em>.
            </h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-brand-muted md:text-base">
              Thank you, {form.name}. Your application for <span className="font-semibold text-brand-ink">{form.course}</span> has
              been received. Our admissions team will call you at {form.phone} within 24 hours.
            </p>
            <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-brand-muted">
              Application ID · <span data-testid="application-id" className="font-semibold text-brand-green">{done.id.slice(0, 8).toUpperCase()}</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                data-testid="admission-whatsapp-button"
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-ink"
              >
                <MessageCircle className="h-4 w-4" /> Chat with Admissions
              </a>
              <a data-testid="admission-home-link" href="/" className="text-sm font-semibold text-brand-green underline decoration-brand-green/30 underline-offset-4 hover:text-brand-terra">
                Back to Website
              </a>
            </div>
          </motion.div>
        ) : (
          <motion.form
            data-testid="admission-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="space-y-8"
          >
            <section className="border border-brand-line bg-brand-surface p-8 shadow-sm md:p-10">
              <h2 className="flex items-center gap-3 font-serif text-2xl font-light">
                <User className="h-5 w-5 text-brand-terra" /> Personal Details
              </h2>
              <div className="mt-7 grid gap-5 md:grid-cols-2">
                <Field label="Full Name *">
                  <input data-testid="adm-name-input" required value={form.name} onChange={set("name")} placeholder="As per 10th certificate" className={inputClass} />
                </Field>
                <Field label="Date of Birth *">
                  <input data-testid="adm-dob-input" required type="date" value={form.dob} onChange={set("dob")} className={inputClass} />
                </Field>
                <Field label="Gender *">
                  <select data-testid="adm-gender-select" value={form.gender} onChange={set("gender")} className={inputClass}>
                    {GENDERS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Religion *">
                  <select data-testid="adm-religion-select" value={form.religion} onChange={set("religion")} className={inputClass}>
                    {RELIGIONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Mobile Number (WhatsApp) *">
                  <input data-testid="adm-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputClass} />
                </Field>
                <Field label="Email Address *">
                  <input data-testid="adm-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputClass} />
                </Field>
              </div>
            </section>

            <section className="border border-brand-line bg-brand-surface p-8 shadow-sm md:p-10">
              <h2 className="flex items-center gap-3 font-serif text-2xl font-light">
                <MapPin className="h-5 w-5 text-brand-terra" /> Address
              </h2>
              <div className="mt-7 grid gap-5 md:grid-cols-2">
                <Field label="Full Address *" span>
                  <textarea data-testid="adm-address-input" required rows={3} value={form.address} onChange={set("address")} placeholder="House no, street, area..." className={`${inputClass} resize-none`} />
                </Field>
                <Field label="City *">
                  <input data-testid="adm-city-input" required value={form.city} onChange={set("city")} placeholder="City" className={inputClass} />
                </Field>
                <Field label="State *">
                  <input data-testid="adm-state-input" required value={form.state} onChange={set("state")} placeholder="State" className={inputClass} />
                </Field>
                <Field label="Pincode *">
                  <input data-testid="adm-pincode-input" required inputMode="numeric" maxLength={6} value={form.pincode} onChange={set("pincode")} placeholder="110077" className={inputClass} />
                </Field>
                <Field label="Course Applying For *">
                  <select data-testid="adm-course-select" value={form.course} onChange={set("course")} className={inputClass}>
                    {COURSES.map((c) => <option key={c.title} value={c.title}>{c.title}</option>)}
                  </select>
                </Field>
              </div>
            </section>

            <section className="border border-brand-line bg-brand-surface p-8 shadow-sm md:p-10">
              <h2 className="flex items-center gap-3 font-serif text-2xl font-light">
                <FileText className="h-5 w-5 text-brand-terra" /> Document Uploads
              </h2>
              <p className="mt-2 text-xs text-brand-muted">Clear scans or photos · PNG, JPG or PDF · max 3 MB each · 10th certificate & Aadhaar are mandatory</p>
              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <UploadBox label="10th Certificate" hint="Upload 10th certificate" required value={cert10} onPick={pick(setCert10)} testId="adm-cert10-upload" />
                <UploadBox label="ID Proof (Aadhaar)" hint="Upload Aadhaar card" required value={idProof} onPick={pick(setIdProof)} testId="adm-id-upload" />
                <UploadBox label="Passport Photo (optional)" hint="Upload photo (PNG/JPG)" value={photo} onPick={pick(setPhoto, true)} testId="adm-photo-upload" />
                <UploadBox label="12th Certificate (optional)" hint="Upload 12th certificate" value={cert12} onPick={pick(setCert12)} testId="adm-cert12-upload" />
              </div>
            </section>

            <button
              data-testid="admission-submit-button"
              type="submit"
              disabled={sending}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-green px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-ink disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
              {sending ? "Submitting Application..." : "Submit Admission Application"}
            </button>
            <p className="text-center text-xs text-brand-muted">
              By submitting, you agree to be contacted by our admissions team on call, email and WhatsApp.
            </p>
          </motion.form>
        )}
      </main>

      <footer className="border-t border-brand-line py-8 text-center text-xs text-brand-muted">
        © 2026 Abhi Institute of Hotel Management · Dwarka, New Delhi · +91-9971494317
      </footer>
    </div>
  );
}
